/**
 * 实例 / 篇章内容的编辑弹窗：JSON 字段保存前先校验格式，文本字段带字数统计
 */
import { useState } from 'react'
import { App, Input, Modal, Typography } from 'antd'
import styles from './UserInstances.module.css'

export type EditMode = 'json' | 'text'

export interface EditTarget {
  title: string
  mode: EditMode
  initialValue: string
  // 文本模式的占位提示和字数上限
  placeholder?: string
  maxLength?: number
  hint?: string
  rows: number
  // 保存：JSON 模式传入解析后的对象，文本模式传入字符串
  onSave: (value: unknown) => Promise<void>
}

interface EditContentModalProps {
  target: EditTarget | null
  onClose: () => void
}

export function EditContentModal({ target, onClose }: Readonly<EditContentModalProps>) {
  return target ? <EditContentForm key={target.title} target={target} onClose={onClose} /> : null
}

function EditContentForm({ target, onClose }: Readonly<{ target: EditTarget; onClose: () => void }>) {
  const { message } = App.useApp()
  const [content, setContent] = useState(target.initialValue)
  const [saving, setSaving] = useState(false)

  const handleOk = async () => {
    let value: unknown = content
    if (target.mode === 'json') {
      try {
        value = JSON.parse(content)
      } catch {
        message.error('JSON 格式错误，请检查')
        return
      }
    }
    setSaving(true)
    try {
      await target.onSave(value)
      message.success('更新成功')
      onClose()
    } catch (error: any) {
      console.error('Failed to update content:', error)
      message.error(error.response?.data?.detail || '更新失败')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      title={target.title}
      width={target.mode === 'json' ? 700 : 800}
      okButtonProps={{ loading: saving }}
      onOk={handleOk}
      onCancel={onClose}
    >
      <Input.TextArea
        aria-label={target.title}
        rows={target.rows}
        className={target.mode === 'json' ? styles.monospace : undefined}
        placeholder={target.mode === 'json' ? '请输入 JSON 格式内容' : target.placeholder}
        showCount={target.mode === 'text'}
        maxLength={target.mode === 'text' ? target.maxLength : undefined}
        value={content}
        onChange={(event) => setContent(event.target.value)}
      />
      <Typography.Text type="secondary" className={styles.hint}>
        {target.mode === 'json' ? '💡 提示：请输入有效的 JSON 格式' : target.hint}
      </Typography.Text>
    </Modal>
  )
}
